import React, { useState, useEffect } from 'react';
import { TrendingUp, DollarSign, Clock, CheckCircle2, RefreshCw, Download } from 'lucide-react';
import axiosInstance from '@/services/axiosInstance';
import DataToolTip from '@/components/common/DataToolTip';
import ConfirmationModal from '@/components/modals/ConfirmationModal';
import StatDetailModal from '@/components/modals/StatDetailModal';
import { useAuth } from '@/contexts/AuthContext';
import { isSuperAdmin, isAdmin } from '@/utils/roleUtils';
import { downloadMasterExcel } from '@/services/academicService';
import './HomeStats.scss';

const HomeStats = ({
  institution,
  setInstitution,
  renderHeaderOnly = false,
  renderGridOnly = false,
  sidebarMode = false,
  availableInstitutions: propAvailableInstitutions
}) => {
  const { user } = useAuth();
  const [liveStats, setLiveStats] = useState(null);
  const [availableInstitutions, setAvailableInstitutions] = useState(propAvailableInstitutions || []);
  const [loading, setLoading] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedStat, setSelectedStat] = useState(null);

  const fetchInstitutions = async () => {
    try {
      const response = await axiosInstance.get('/api/advising/institutions');
      if (response.data && Array.isArray(response.data)) {
        setAvailableInstitutions(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch institutions:', err);
    }
  };

  const fetchStats = async (targetInst) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/api/advising/stats?institution=${targetInst}`);
      setLiveStats(response.data);
    } catch (err) {
      console.error('Failed to fetch live stats:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (propAvailableInstitutions && propAvailableInstitutions.length > 0) {
      setAvailableInstitutions(propAvailableInstitutions);
    } else {
      fetchInstitutions();
    }
  }, [propAvailableInstitutions]);

  useEffect(() => {
    if (institution) {
      fetchStats(institution);
    }
  }, [institution]);

  const handleReset = async () => {
    setShowConfirmModal(true);
  };

  const confirmReset = async () => {
    setShowConfirmModal(false);
    setIsResetting(true);
    try {
      await axiosInstance.post('/api/advising/stats/reset');
      await fetchStats(institution);
    } catch (err) {
      console.error('Failed to reset stats cache:', err);
      alert('Failed to reset cache. Check console for details.');
    } finally {
      setIsResetting(false);
    }
  };

  const handleDownloadMasterExcel = async () => {
    try {
      const blob = await downloadMasterExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'PolyTechnic_Academic_Intel_Master.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download master Excel file:', error);
      alert('Could not download master Excel file. Ensure you are authorized and logged in as a Super Admin.');
    }
  };

  const statsConfig = [
    {
      id: 'salary',
      label: 'Avg. Career Salary',
      value: liveStats?.avgSalary || '$95,000+',
      subtext: (institution && institution.includes('Memphis')) ? 'Memphis MSA Data' : 'Regional Baseline',
      tooltip: liveStats?.tooltips?.salary || 'Average annual salary for Bachelor of Applied Science graduates in the regional market.',
      longExplanation: `Our intelligence engine synthesizes data from the Bureau of Labor Statistics (BLS) and regional job market postings to project the lifetime earning potential of BAS graduates. \n\nGraduates in specialized concentrations like Cyber Security, Data Analytics, and Manufacturing Technology often exceed the regional median within 3 years of degree completion. This metric reflects a weighted average across all technical pathways offered by the institution.`,
      takeaways: [
        "Competitive with traditional 4-year engineering degrees",
        "Reflects mid-career earning potential",
        "Based on real-time regional labor demand"
      ],
      icon: <TrendingUp size={24} />,
      color: '#22c55e'
    },
    {
      id: 'savings',
      label: 'Tuition Savings',
      value: liveStats?.tuitionSavings || '$8,000',
      subtext: 'Bridge Program Benefit',
      tooltip: liveStats?.tooltips?.savings || 'Estimated tuition savings achieved by completing an AAS degree before transferring.',
      longExplanation: `The "Bridge" strategy is designed to maximize your financial efficiency. By completing your lower-division technical requirements at a community college (AAS) and transferring into the BAS program, you significantly reduce your overall debt. \n\nCalculations include the difference in credit-hour pricing between community college rates and university rates, plus the "Block Transfer" guarantee which ensures you don't pay for redundant courses.`,
      takeaways: [
        "Up to 40% reduction in total degree cost",
        "Guaranteed transfer credit recognition",
        "Eligibility for specific transfer scholarships"
      ],
      icon: <DollarSign size={24} />,
      color: '#ed7534'
    },
    {
      id: 'time',
      label: 'Degree Velocity',
      value: liveStats?.degreeVelocity || '24 Months',
      subtext: 'Time to BAS Completion',
      tooltip: liveStats?.tooltips?.velocity || 'Projected timeline to degree completion for students following the full-time accelerated pathway.',
      longExplanation: `We measure "Velocity" as the time from your official transfer enrollment to the completion of all 120 credit hours. The BAS program is uniquely structured for working professionals, offering 8-week accelerated terms and year-round course availability. \n\nMost students who enter with a completed AAS degree and follow our AI-optimized roadmap graduate within 18 to 24 months, even while working full-time.`,
      takeaways: [
        "Accelerated 8-week course formats",
        "Continuous enrollment options",
        "Optimized course sequencing to avoid bottlenecks"
      ],
      icon: <Clock size={24} />,
      color: '#3b82f6'
    },
    {
      id: 'efficiency',
      label: 'Transfer Efficiency',
      value: liveStats?.transferEfficiency || '94%+',
      subtext: 'IT Pathway Guarantee',
      tooltip: liveStats?.tooltips?.efficiency || 'Percentage of IT credits guaranteed to transfer directly into the BAS concentration.',
      longExplanation: `Transfer Efficiency is the hallmark of the PolyTechnic initiative. Unlike traditional degrees where technical credits are often "lost" during transfer, our alignment with regional community colleges ensures that your hands-on technical skills are treated as academic gold. \n\nWe achieve this through rigorous "cross-walk" mapping where your AAS technical core directly satisfies the first two years of the BAS degree, leaving only upper-division requirements.`,
      takeaways: [
        "Minimum of 60 credits accepted in block transfer",
        "Direct mapping of TCAT and CC technical core",
        'Elimination of "hidden" prerequisite requirements'
      ],
      icon: <CheckCircle2 size={24} />,
      color: '#8b5cf6'
    }
  ];

  // ── 1. RENDER HEADER ONLY MODE ──
  if (renderHeaderOnly) {
    return (
      <div className={`home-stats-container header-only ${loading ? 'loading' : ''}`} style={{ marginBottom: '1.5rem', marginTop: '1rem' }}>
        <div className="stats-header" style={{ justifyContent: 'center', margin: 0 }}>
          <div className="institution-selector">
            <span className="selector-label">TARGET INSTITUTION:</span>
            {showSelector ? (
              <select
                className="stats-inst-select"
                value={institution || ''}
                onChange={(e) => {
                  const selected = e.target.value;
                  setInstitution(selected);
                  setShowSelector(false);
                }}
                style={{ marginLeft: '0.5rem', fontWeight: 800, textTransform: 'uppercase', fontSize: '0.9rem' }}
              >
                <option value="" disabled>Select institution</option>
                {availableInstitutions.map((inst) => (
                  <option key={inst.shortName || inst} value={inst.shortName || inst}>
                    {inst.fullName || inst.shortName || inst}
                  </option>
                ))}
              </select>
            ) : (
              <span className="institution-static-value" style={{ fontWeight: 800, color: '#1a202c', marginLeft: '0.5rem', letterSpacing: '-0.01em', textTransform: 'uppercase', fontSize: '0.9rem' }}>
                {institutionName || 'Select an institution'}
                <button
                  className="select-institution-btn"
                  onClick={() => setShowSelector(true)}
                  style={{ marginLeft: '0.5rem', background: 'none', border: 'none', color: '#0055ff', cursor: 'pointer' }}
                >
                  (change)
                </button>
              </span>
            )}
          </div>

          {isSuperAdmin(user) && (
            <button
              className={`admin-refresh-btn ${isResetting ? 'spinning' : ''}`}
              onClick={handleReset}
              disabled={isResetting || loading}
              title="Force Global Cache Reset (Super Admin Only)"
            >
              <RefreshCw size={18} />
              <span>{isResetting ? 'Resetting...' : 'Sync Data'}</span>
            </button>
          )}
        </div>

        <ConfirmationModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={confirmReset}
          title="Reset Global Cache?"
          message="This will force a re-calculation of institutional statistics for all users. This action cannot be undone."
          confirmText="Reset Now"
          type="warning"
        />
      </div>
    );
  }

  // ── 2. RENDER GRID ONLY MODE ──
  if (renderGridOnly) {
    return (
      <div className={`home-stats-container grid-only ${sidebarMode ? 'sidebar-mode' : ''} ${loading ? 'loading' : ''}`} style={sidebarMode ? { marginTop: 0, marginBottom: 0 } : { marginTop: '2.5rem', marginBottom: '1.5rem' }}>
        <div className="stats-grid">
          {statsConfig.map((stat) => (
            <DataToolTip key={stat.id} text={stat.tooltip} direction="up">
              <div className="stat-card" onClick={() => setSelectedStat(stat)}>
                <div className="stat-icon-wrapper" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                  {stat.icon}
                </div>
                <div className="stat-info">
                  <span className="stat-value">{stat.value}</span>
                  <span className="stat-label">{stat.label}</span>
                  <span className="stat-subtext">{stat.subtext}</span>
                </div>
              </div>
            </DataToolTip>
          ))}
        </div>

        <StatDetailModal
          isOpen={!!selectedStat}
          onClose={() => setSelectedStat(null)}
          stat={selectedStat}
          institutionName={availableInstitutions.find(inst => (inst.shortName || inst) === institution)?.fullName || institution}
        />
      </div>
    );
  }

  // ── 3. DEFAULT MODE (SUPPORTING SIDEBAR MODE) ──
  return (
    <div className={`home-stats-container ${sidebarMode ? 'sidebar-mode' : ''} ${loading ? 'loading' : ''}`}>
      <div className="stats-header">
        <div className="institution-selector">
          <span className="selector-label">TARGET INSTITUTION:</span>
          <select
            className="stats-inst-select"
            value={institution}
            onChange={(e) => setInstitution(e.target.value)}
            disabled={availableInstitutions.length === 0 || isResetting}
          >
            {availableInstitutions.map(inst => (
              <option key={inst.shortName || inst} value={inst.shortName || inst}>
                {inst.fullName || inst.shortName || inst}
              </option>
            ))}
          </select>
        </div>

        {(isSuperAdmin(user) || isAdmin(user)) && (
          <>
            {isSuperAdmin(user) && (
              <button
                className={`admin-refresh-btn ${isResetting ? 'spinning' : ''}`}
                onClick={handleReset}
                disabled={isResetting || loading}
                title="Force Global Cache Reset (Super Admin Only)"
              >
                <RefreshCw size={18} />
                <span>{isResetting ? 'Resetting...' : 'Sync Data'}</span>
                <span className="admin-badge" data-tooltip="Super Admin Exclusive Access">SA</span>
              </button>
            )}
            {isAdmin(user) && (
              <button
                className="admin-download-btn"
                onClick={handleDownloadMasterExcel}
                disabled={isResetting || loading}
                title="Download Master Academic Intel Excel (Admin Only)"
              >
                <Download size={18} />
                <span>Download Master Excel</span>
                <span className="admin-badge" data-tooltip="Institutional Admin Access">A</span>
              </button>
            )}
          </>
        )}
      </div>

      <div className="stats-grid">
        {statsConfig.map((stat) => (
          <DataToolTip key={stat.id} text={stat.tooltip} direction="up">
            <div className="stat-card" onClick={() => setSelectedStat(stat)}>
              <div className="stat-icon-wrapper" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                {stat.icon}
              </div>
              <div className="stat-info">
                <span className="stat-value">{stat.value}</span>
                <span className="stat-label">{stat.label}</span>
                <span className="stat-subtext">{stat.subtext}</span>
              </div>
            </div>
          </DataToolTip>
        ))}
      </div>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmReset}
        title="Reset Global Cache?"
        message="This will force a re-calculation of institutional statistics for all users. This action cannot be undone."
        confirmText="Reset Now"
        type="warning"
      />

      <StatDetailModal
        isOpen={!!selectedStat}
        onClose={() => setSelectedStat(null)}
        stat={selectedStat}
        institutionName={availableInstitutions.find(inst => (inst.shortName || inst) === institution)?.fullName || institution}
      />
    </div>
  );
};

export default HomeStats;
